import { useEffect, useState } from "react";
import type {
  AdminStudentsResponseDto,
  FamilyAccountsResponseDto,
  GuardianLinkRequestDto
} from "@madamuh/domain";
import { useNavigate, useParams } from "react-router-dom";
import { fetchAdminStudents, fetchFamilyAccounts } from "../../shared/api/adminRoster";
import {
  approveGuardianLinkRequest,
  fetchAdminGuardianLinkRequests,
  rejectGuardianLinkRequest
} from "../../shared/api/guardian";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";

const adminLinks = [
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
  { to: "/t/:tenantSlug/teacher/admin/guardian-links", label: "紐付け一覧" }
];

type ApprovalFormState = {
  studentIds: string[];
  familyAccountId: string;
  internalNote: string;
};

function createInitialApprovalFormState(request: GuardianLinkRequestDto): ApprovalFormState {
  return {
    studentIds: [...request.resolvedStudentIds],
    familyAccountId: request.resolvedFamilyAccountId ?? "",
    internalNote: ""
  };
}

function mergeApprovalFormStates(
  requests: GuardianLinkRequestDto[],
  current: Record<string, ApprovalFormState>
): Record<string, ApprovalFormState> {
  return Object.fromEntries(
    requests.map((request) => [
      request.requestId,
      current[request.requestId] ?? createInitialApprovalFormState(request)
    ])
  );
}

function isLikelyStudentMatch(
  request: GuardianLinkRequestDto,
  student: AdminStudentsResponseDto["items"][number]
): boolean {
  if (request.studentName.trim().toLowerCase() === student.displayName.trim().toLowerCase()) {
    return true;
  }

  if (request.birthDate && request.birthDate === student.birthDate) {
    return true;
  }

  return false;
}

export function AdminGuardianLinkRequestsPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [requests, setRequests] = useState<GuardianLinkRequestDto[]>([]);
  const [students, setStudents] = useState<AdminStudentsResponseDto | null>(null);
  const [familyAccounts, setFamilyAccounts] = useState<FamilyAccountsResponseDto | null>(null);
  const [approvalForms, setApprovalForms] = useState<Record<string, ApprovalFormState>>({});
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyRequestId, setBusyRequestId] = useState<string | null>(null);

  useEffect(() => {
    setApprovalForms((current) => mergeApprovalFormStates(requests, current));
  }, [requests]);

  useEffect(() => {
    if (!isSessionReady || !session?.member?.roles.includes("admin")) {
      return;
    }

    let cancelled = false;

    async function loadRequests() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const [requestsResponse, studentsResponse, familyAccountsResponse] = await Promise.all([
          fetchAdminGuardianLinkRequests(tenantSlug),
          fetchAdminStudents(tenantSlug),
          fetchFamilyAccounts(tenantSlug)
        ]);

        if (cancelled) {
          return;
        }

        setRequests(requestsResponse.items);
        setStudents(studentsResponse);
        setFamilyAccounts(familyAccountsResponse);
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (isUnauthenticatedApiError(error)) {
          navigate(`/t/${tenantSlug}/entry`, { replace: true });
          return;
        }

        setLoadState("error");
        setLoadError(getProtectedApiErrorMessage(error, "申請一覧の取得に失敗しました"));
      }
    }

    void loadRequests();

    return () => {
      cancelled = true;
    };
  }, [isSessionReady, navigate, session, tenantSlug]);

  async function reloadRequests() {
    try {
      const response = await fetchAdminGuardianLinkRequests(tenantSlug);
      setRequests(response.items);
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      throw error;
    }
  }

  function updateApprovalForm(
    requestId: string,
    updater: (current: ApprovalFormState) => ApprovalFormState
  ) {
    setApprovalForms((current) => {
      const target = current[requestId];

      if (!target) {
        return current;
      }

      return {
        ...current,
        [requestId]: updater(target)
      };
    });
  }

  function toggleSelectedStudent(requestId: string, studentId: string, checked: boolean) {
    updateApprovalForm(requestId, (current) => {
      const nextStudentIds = checked
        ? [...new Set([...current.studentIds, studentId])]
        : current.studentIds.filter((currentStudentId) => currentStudentId !== studentId);

      return {
        ...current,
        studentIds: nextStudentIds
      };
    });
  }

  async function handleReview(
    requestId: string,
    action: "approve" | "reject"
  ) {
    setBusyRequestId(requestId);
    setLoadError(null);

    try {
      if (action === "approve") {
        const approvalForm = approvalForms[requestId];

        if (!approvalForm || approvalForm.studentIds.length === 0) {
          setLoadError("承認時は対象 student を 1件以上選択してください。");
          return;
        }

        await approveGuardianLinkRequest(tenantSlug, requestId, {
          studentIds: approvalForm.studentIds,
          familyAccountId: approvalForm.familyAccountId || null,
          internalNote: approvalForm.internalNote.trim() || null
        });
      } else {
        await rejectGuardianLinkRequest(tenantSlug, requestId);
      }

      await reloadRequests();
    } catch (error) {
      setLoadError(getProtectedApiErrorMessage(error, "レビュー処理に失敗しました"));
    } finally {
      setBusyRequestId(null);
    }
  }

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title="Admin Guardian Link Requests"
      description="申請送信だけではまだ生徒リンクも parent role も作成されません。admin が student と FamilyAccount を確認して承認した時点で、link と parent role が作成されます。"
      links={adminLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p>Entry へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !session?.member?.roles.includes("admin") ? (
          <p>この画面は admin role のみ利用できます。</p>
        ) : null}
        {loadState === "loading" ? <p>申請一覧を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}

        {routeStatus === "authenticated" && session?.member?.roles.includes("admin") ? (
          <div style={{ display: "grid", gap: 12 }}>
            <section
              style={{
                padding: 12,
                border: "1px solid #e5e5e5",
                borderRadius: 12,
                background: "#fafafa",
                display: "grid",
                gap: 6
              }}
            >
              <strong>review rule</strong>
              <span style={{ fontSize: 14, opacity: 0.8 }}>
                request submit alone では、FamilyAccount / userStudentLinks / parent role は作成されません。
                FamilyAccount は申請者ではなく admin が選択または事前作成します。
              </span>
            </section>
            {requests.length === 0 ? <p style={{ margin: 0 }}>未処理の申請はありません。</p> : null}

            {requests.map((request) => {
              const isBusy = busyRequestId === request.requestId;
              const approvalForm = approvalForms[request.requestId] ?? createInitialApprovalFormState(request);
              const sortedStudents = [...(students?.items ?? [])].sort((left, right) => {
                const leftMatch = isLikelyStudentMatch(request, left) ? 0 : 1;
                const rightMatch = isLikelyStudentMatch(request, right) ? 0 : 1;

                if (leftMatch !== rightMatch) {
                  return leftMatch - rightMatch;
                }

                return left.displayName.localeCompare(right.displayName, "ja");
              });

              return (
                <article
                  key={request.requestId}
                  style={{
                    border: "1px solid #e5e5e5",
                    borderRadius: 12,
                    padding: 12,
                    display: "grid",
                    gap: 8
                  }}
                >
                  <div>
                    <strong>{request.studentName}</strong> / {request.requestedRelationType}
                  </div>
                  <div>requestId: {request.requestId}</div>
                  <div>status: {request.status}</div>
                  <div>birthDate: {request.birthDate ?? "not provided"}</div>
                  <div>supportingInfo: {request.supportingInfo ?? "none"}</div>
                  <div>resolvedStudentIds: {request.resolvedStudentIds.join(", ") || "none"}</div>
                  <div>resolvedFamilyAccountId: {request.resolvedFamilyAccountId ?? "none"}</div>
                  <div style={{ fontSize: 14, opacity: 0.8 }}>
                    申請だけではまだ生徒表示は開始されません。承認時に選択した student のみを紐付けます。
                  </div>

                  {request.status === "submitted" ? (
                    <div style={{ display: "grid", gap: 10 }}>
                      <div style={{ display: "grid", gap: 6 }}>
                        <strong style={{ fontSize: 14 }}>承認する student</strong>
                        <div style={{ display: "grid", gap: 6 }}>
                          {sortedStudents.map((student) => {
                            const checked = approvalForm.studentIds.includes(student.studentId);
                            const likelyMatch = isLikelyStudentMatch(request, student);

                            return (
                              <label
                                key={student.studentId}
                                style={{
                                  display: "grid",
                                  gap: 2,
                                  padding: 8,
                                  border: "1px solid #ececec",
                                  borderRadius: 10
                                }}
                              >
                                <span style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(event) =>
                                      toggleSelectedStudent(
                                        request.requestId,
                                        student.studentId,
                                        event.target.checked
                                      )
                                    }
                                  />
                                  <span>{student.displayName}</span>
                                  {likelyMatch ? (
                                    <span style={{ fontSize: 12, opacity: 0.75 }}>候補一致</span>
                                  ) : null}
                                </span>
                                <span style={{ fontSize: 12, opacity: 0.75 }}>
                                  {student.birthDate} / family: {student.familyAccountDisplayName ?? "未設定"} / {student.studentId}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>

                      <label style={{ display: "grid", gap: 4 }}>
                        <span>FamilyAccount</span>
                        <select
                          value={approvalForm.familyAccountId}
                          onChange={(event) =>
                            updateApprovalForm(request.requestId, (current) => ({
                              ...current,
                              familyAccountId: event.target.value
                            }))
                          }
                        >
                          <option value="">選択しない</option>
                          {familyAccounts?.items.map((familyAccount) => (
                            <option key={familyAccount.familyAccountId} value={familyAccount.familyAccountId}>
                              {familyAccount.displayName} / {familyAccount.familyAccountId}
                            </option>
                          ))}
                        </select>
                        <span style={{ fontSize: 12, opacity: 0.8 }}>
                          FamilyAccount は admin が選択します。必要なら先に生徒画面で作成してください。
                        </span>
                      </label>

                      <label style={{ display: "grid", gap: 4 }}>
                        <span>internalNote</span>
                        <textarea
                          value={approvalForm.internalNote}
                          onChange={(event) =>
                            updateApprovalForm(request.requestId, (current) => ({
                              ...current,
                              internalNote: event.target.value
                            }))
                          }
                          rows={3}
                          placeholder="承認メモや照合根拠"
                        />
                      </label>

                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button
                          type="button"
                          onClick={() => void handleReview(request.requestId, "approve")}
                          disabled={isBusy || approvalForm.studentIds.length === 0}
                        >
                          {isBusy ? "処理中..." : "承認してリンクを作成"}
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleReview(request.requestId, "reject")}
                          disabled={isBusy}
                        >
                          {isBusy ? "処理中..." : "申請を却下"}
                        </button>
                      </div>
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
